/**
 * Rapid X Platform Login
 * HQ Gateway Presentation
 *
 * Authentication behaviour preserved:
 * - platformLogin()
 * - /platform/auth/change-password
 * - must_change_password
 * - platform navigation
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Shield,
  Loader2,
  AlertCircle,
  KeyRound,
  LockKeyhole,
} from 'lucide-react';

export default function PlatformLoginPage() {
  const { platformLogin, api, user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Password change state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Existing authenticated-user behaviour
  React.useEffect(() => {
    if (user && user.must_change_password) {
      setMustChangePassword(true);
    } else if (user && !user.must_change_password) {
      navigate('/platform', { replace: true });
    }
  }, [user, navigate]);

  // Existing platform authentication
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await platformLogin(email, password);

    setLoading(false);

    if (result.success) {
      if (result.must_change_password) {
        setMustChangePassword(true);
        setCurrentPassword(password);
      } else {
        navigate('/platform');
      }
    } else {
      setError(result.error);
    }
  };

  // Existing mandatory password-change workflow
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return;
    }

    setChangingPassword(true);

    try {
      await api.post('/platform/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      updateUser({ must_change_password: false });
      navigate('/platform', { replace: true });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        'Failed to change password'
      );
    } finally {
      setChangingPassword(false);
    }
  };

  /*
   * Shared full-screen HQ presentation.
   *
   * Desktop:
   *   building / branding = left
   *   helicopter          = centre
   *   authentication      = right
   *
   * Mobile:
   *   centred authentication panel
   */
  const pageStyle = {
    minHeight: '100vh',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    backgroundImage: `
      linear-gradient(
        90deg,
        rgba(1, 5, 12, 0.04) 0%,
        rgba(1, 5, 12, 0.06) 38%,
        rgba(1, 5, 12, 0.14) 56%,
        rgba(1, 5, 12, 0.56) 76%,
        rgba(1, 5, 12, 0.86) 100%
      ),
      url("/assets/rapidx-hq-v12.png")
    `,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
  };


  const experimentalStyles = `
    /* RAPID_X_V12_ANIMATION_STYLES */

    @keyframes rapidxPanelPulse {
      0%, 100% {
        box-shadow:
          0 26px 80px rgba(0,0,0,.58),
          0 0 0 1px rgba(245,158,11,.16),
          0 0 18px rgba(245,158,11,.05);
      }

      50% {
        box-shadow:
          0 30px 95px rgba(0,0,0,.66),
          0 0 0 1px rgba(245,158,11,.40),
          0 0 40px rgba(245,158,11,.22);
      }
    }

    @keyframes rapidxLightSweep {
      0% {
        transform: translateX(-140%) skewX(-18deg);
        opacity: 0;
      }

      12% {
        opacity: .18;
      }

      35% {
        opacity: .07;
      }

      60%, 100% {
        transform: translateX(220%) skewX(-18deg);
        opacity: 0;
      }
    }

    @keyframes rapidxButtonGlow {
      0%, 100% {
        box-shadow:
          0 0 0 rgba(245,158,11,0),
          0 8px 25px rgba(0,0,0,.35);
      }

      50% {
        box-shadow:
          0 0 24px rgba(245,158,11,.32),
          0 10px 30px rgba(0,0,0,.40);
      }
    }

    @keyframes rapidxBrandPulse {
      0%, 100% {
        filter: drop-shadow(0 0 0 rgba(239,68,68,0));
      }

      50% {
        filter: drop-shadow(0 0 8px rgba(239,68,68,.28));
      }
    }

    @keyframes rapidxAtmosphere {
      0% {
        transform: translate3d(-1%, 0, 0) scale(1.02);
        opacity: .20;
      }

      50% {
        transform: translate3d(1.2%, -0.6%, 0) scale(1.04);
        opacity: .34;
      }

      100% {
        transform: translate3d(-1%, 0, 0) scale(1.02);
        opacity: .20;
      }
    }

    .rapidx-v12-panel {
      animation: rapidxPanelPulse 5.5s ease-in-out infinite;
    }

    .rapidx-v12-sweep {
      animation: rapidxLightSweep 7s ease-in-out infinite;
    }

    .rapidx-v12-button {
      animation: rapidxButtonGlow 4s ease-in-out infinite;
    }

    .rapidx-v12-brand {
      animation: rapidxBrandPulse 3.8s ease-in-out infinite;
    }

    .rapidx-v12-atmosphere {
      animation: rapidxAtmosphere 12s ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
      .rapidx-v12-panel,
      .rapidx-v12-sweep,
      .rapidx-v12-button,
      .rapidx-v12-brand,
      .rapidx-v12-atmosphere {
        animation: none !important;
      }
    }
  `;


  /*
   * Password change page
   */
  if (mustChangePassword) {
    return (
      <div
        data-testid="platform-change-password-page"
        style={pageStyle}
        className="
          flex
          min-h-screen
          items-center
          justify-center
          px-4
          py-8
          lg:justify-end
          lg:px-[5vw]
        "
      >
        <div
          className="
            relative
            z-10
            w-full
            max-w-[410px]
            rounded-2xl
            border
            border-white/15
            bg-black/68
            px-7
            py-6
            shadow-2xl
            backdrop-blur-xl
            sm:px-8
            sm:py-7
          "
        >
          <div className="text-center mb-6">
            <div
              className="
                inline-flex
                items-center
                justify-center
                w-14
                h-14
                rounded-xl
                bg-amber-500/10
                border
                border-amber-500/30
                mb-4
              "
            >
              <KeyRound className="w-7 h-7 text-amber-500" />
            </div>

            <h1 className="text-2xl font-bold text-white">
              Change Password
            </h1>

            <p className="text-sm text-zinc-400 mt-1.5">
              You must set a new password before accessing the platform.
            </p>
          </div>

          <form
            onSubmit={handleChangePassword}
            className="space-y-4"
          >
            <div>
              <Label className="text-zinc-300 text-xs">
                New Password
              </Label>

              <Input
                type="password"
                required
                autoFocus
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="
                  bg-black/60
                  border-white/20
                  text-white
                  mt-1
                  h-11
                "
                placeholder="Min 8 characters"
                data-testid="new-password-input"
              />
            </div>

            <div>
              <Label className="text-zinc-300 text-xs">
                Confirm New Password
              </Label>

              <Input
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="
                  bg-black/60
                  border-white/20
                  text-white
                  mt-1
                  h-11
                "
                data-testid="confirm-password-input"
              />
            </div>

            {error && (
              <div
                className="
                  flex
                  items-center
                  gap-2
                  text-red-300
                  text-sm
                  bg-red-500/10
                  border
                  border-red-500/30
                  rounded-md
                  px-3
                  py-2
                "
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={changingPassword}
              className="
                w-full
                h-11
                bg-amber-600
                hover:bg-amber-500
                text-white
                font-semibold
              "
              data-testid="change-password-submit"
            >
              {changingPassword ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}

              Set New Password
            </Button>
          </form>
        </div>
      </div>
    );
  }

  /*
   * Main Platform Login
   */
  return (
    <div
      data-testid="platform-login-page"
      style={pageStyle}
      className="
        flex
        min-h-screen
        items-center
        justify-center
        px-4
        py-8
        lg:justify-end
        lg:px-[5vw]
      "
    >
      <style>{experimentalStyles}</style>
      <div
        aria-hidden="true"
        className="
          rapidx-v12-atmosphere
          pointer-events-none
          absolute
          inset-[-3%]
          bg-[radial-gradient(circle_at_72%_24%,rgba(245,158,11,0.10),transparent_28%),radial-gradient(circle_at_48%_55%,rgba(59,130,246,0.08),transparent_35%)]
        "
      />

      {/* Subtle cinematic lower vignette */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          inset-0
          bg-gradient-to-t
          from-black/35
          via-transparent
          to-black/10
        "
      />

      {/* Login console */}
      <div
        className="
          rapidx-v12-panel
          relative
          z-10
          w-full
          max-w-[410px]
          rounded-2xl
          border
          border-white/15
          bg-black/68
          px-7
          py-6
          shadow-2xl
          backdrop-blur-xl
          sm:px-8
          sm:py-7
        "
      >
        <div
          aria-hidden="true"
          className="
            rapidx-v12-sweep
            pointer-events-none
            absolute
            -left-1/3
            top-0
            h-full
            w-1/3
            bg-gradient-to-r
            from-transparent
            via-white/10
            to-transparent
          "
        />

        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute
            left-10
            right-10
            top-0
            h-px
            bg-gradient-to-r
            from-transparent
            via-amber-500/60
            to-transparent
          "
        />

        <div className="text-center mb-6">

          <div
            className="
              rapidx-v12-brand
              inline-flex
              items-center
              justify-center
              h-14
              min-w-[68px]
              px-3
              rounded-xl
              bg-black/45
              border
              border-amber-500/30
              mb-3
              shadow-lg
            "
            aria-label="Rapid X"
          >
            <span
              className="
                text-[26px]
                font-black
                italic
                tracking-[-0.12em]
                text-white
                leading-none
              "
            >
              R
            </span>
            <span
              className="
                text-[30px]
                font-black
                italic
                tracking-[-0.10em]
                text-red-500
                leading-none
              "
            >
              X
            </span>
          </div>

          <h1
            className="
              text-[28px]
              font-bold
              tracking-tight
              text-white
            "
          >
            Rapid X
          </h1>

          <p
            className="
              text-[11px]
              text-amber-500
              font-mono
              uppercase
              tracking-[0.20em]
              mt-2
            "
          >
            Platform Administration
          </p>

          <div className="flex items-center justify-center gap-2 mt-3">
            <div className="h-px w-10 bg-amber-500/40" />

            <LockKeyhole className="w-3 h-3 text-zinc-500" />

            <div className="h-px w-10 bg-amber-500/40" />
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <div>
            <Label
              htmlFor="email"
              className="text-zinc-300 text-xs"
            >
              Email
            </Label>

            <Input
              id="email"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="
                bg-black/60
                border-white/20
                text-white
                placeholder:text-zinc-600
                mt-1.5
                h-11
                focus:border-amber-500/60
              "
              placeholder="admin@rapidxtech.com.au"
              data-testid="platform-login-email"
            />
          </div>

          <div>
            <Label
              htmlFor="password"
              className="text-zinc-300 text-xs"
            >
              Password
            </Label>

            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="
                bg-black/60
                border-white/20
                text-white
                mt-1.5
                h-11
                focus:border-amber-500/60
              "
              data-testid="platform-login-password"
            />
          </div>

          {error && (
            <div
              className="
                flex
                items-center
                gap-2
                text-red-300
                text-sm
                bg-red-500/10
                border
                border-red-500/30
                rounded-md
                px-3
                py-2
              "
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="
              rapidx-v12-button
              w-full
              h-11
              bg-amber-600
              hover:bg-amber-500
              text-white
              font-semibold
              shadow-lg
              shadow-black/30
            "
            data-testid="platform-login-submit"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : null}

            Sign in to Platform
          </Button>
        </form>

        <div className="mt-6 pt-4 border-t border-white/15">
          <p
            className="
              text-center
              text-[10px]
              leading-relaxed
              text-zinc-500
              font-mono
            "
          >
            For tenant user access, use your organisation's assigned subdomain.
          </p>
        </div>

        <div
          className="
            mt-4
            flex
            items-center
            justify-center
            gap-2
            text-[9px]
            uppercase
            tracking-[0.18em]
            text-zinc-600
          "
        >
          <Shield className="w-3 h-3" />
          Rapid X Secure Platform
        </div>
      </div>
    </div>
  );
}
